import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import ReactLoading from "react-loading";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { RoomCollection } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { RoomGuest, RoomGuestListType } from "@lifeat/shared/data-models/Rooms/RoomGuest";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { isUserActiveProSubscriber } from "@lifeat/shared/user/UserUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import ButtonDivider from "@common/LifeAtButton/ButtonDivider";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { useDatabaseContext } from "@contexts/DatabaseContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import useDatabaseState from "@hooks/useDatabaseState";
import useDbValueListener from "@hooks/useDbValueListener";
import CameraOnIcon from "@icons/VideoCamera.svg";
import { logError } from "@service/ConsoleLoggerService";
import Styles from "@themes/Styles";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { openLinkNewTab, requestMediaPermissions } from "@util/BrowserUtil";
import RoomSelectorDropdown from "../../../../app/LifeAtApp/TopNavigation/TopNavigationComponents/RoomSelectorDropdown";
import { JoinRoomCallEvent, JoinRoomEvent } from "../../AmplitudeRoomsEvents";
import { RoomControls } from "../../RoomController";
import * as RoomService from "../../RoomService";
import { useRoomSettingsContext } from "../../RoomSettingsContext";
import VideoChatContext from "../../VideoChatContext";
import { ChatContextType, useChatContext } from "../../room-chat/ChatContext";
import RoomInviteModal from "../RoomInviteModal";
import RoomPresence from "../RoomPresence/RoomPresence";
import RoomVideoChat from "./RoomVideoChat";

const ControlBarWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    align-items: center;

    min-height: ${Styles.overlayButtonHeight};
    height: ${Styles.overlayButtonHeight};
    max-height: ${Styles.overlayButtonHeight};

    background-color: ${(props) => props.theme.backgroundPrimary};
    border-radius: ${Styles.borderRadiusNormal};
`;

const SocialControls = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 6px;

    height: 32px;
    width: auto;
`;

const ControlBarButtonDivider = styled(ButtonDivider)`
    height: 80%;
    margin: unset;
`;

const CameraIcon = styled(CameraOnIcon)`
    fill: ${(props) => props.theme.textColorPrimary};
    transform: scale(1.3);
`;

const REQUIRES_MEDIA_PERMISSIONS_ERROR = "Video call requires access to your camera and microphone.";
const BLOCKED_CALL_TOOLTIP_TEXT = "Uh-oh. Looks like you have been muted, and can only view this community";
const VIDEO_CALL_BLOCKED_PRO_TOOLTIP = "Upgrade for access to video call";
const VIDEO_CALL_DISABLED_TOOLTIP_TEXT =
    "Video calling is disabled for this room. The room host can enable video calling in their room settings.";
const MIC_HELPDESK_LINK = "https://lifeat.io/en-us/article/how-to-allow-mic-and-video-access-1njqm8o/";

interface RoomPresenceControlsProps {
    readonly roomId: string;
    readonly roomController: RoomControls;
}
export default function RoomPresenceControls(props: RoomPresenceControlsProps) {
    const { roomId, roomController } = { ...props };

    const amplitudeService = AmplitudeService.getInstance();
    const { setUpgradeCtaContext } = useCallToActionContext();

    const { setIsChatOpen }: ChatContextType = useChatContext();
    const { isVideoCallingDisabled, isGuestsCameraDisabled, isGuestsMicDisabled, isScreenShareDisabled } =
        useRoomSettingsContext();
    const { user, userData }: UserContextType = useUserContext();

    const databaseState = useDatabaseState();
    const db = useDatabaseContext();

    const videoCallParticipants = useDbValueListener(RoomCollection.at(roomId).at("videoCallParticipants").node);
    const [isVideoChatLoading, setIsVideoChatLoading] = useState(false);

    const roomIsOwnedByUser = isRoomOwnedByUser(roomId, userData);

    const triggerJoinRoomEventRef = useRef<boolean | null>(null);
    const logJoinRoomEventForNonHosts = () => {
        if (!roomIsOwnedByUser && !triggerJoinRoomEventRef.current) {
            amplitudeService.logEvent(
                new JoinRoomEvent({
                    roomId: props.roomId,
                    performedAsHost: roomIsOwnedByUser,
                    numRoomParticipants: Object.keys(roomController?.roomInfo?.guests ?? {}).length,
                }),
            );
            triggerJoinRoomEventRef.current = true;
        }
    };

    // Handle listening for changes to the Room's status, so we can perform the appropriate action.
    useEffect(() => {
        const inRoom = user && userData && props.roomId && databaseState === "connected";
        if (inRoom) {
            RoomService.handleRoomEnter(props.roomId, user.uid, userData).then(logJoinRoomEventForNonHosts);
        }
    }, [user, userData, props.roomId, databaseState]);

    const [roomGuests, setRoomGuests] = useState<RoomGuestListType>(roomController?.roomInfo?.guests ?? {});

    useEffect(() => {
        const roomGuestsRef = RoomService.roomGuestsRef(props.roomId);
        const roomGuestsListener = RoomService.roomGuestsListener(
            props.roomId,
            (newGuests: { [id: string]: RoomGuest }) => setRoomGuests(newGuests),
        );

        return () => {
            roomGuestsRef.off("value", roomGuestsListener);
        };
    }, [props.roomId]);

    useEffect(() => {
        if (!roomIsOwnedByUser && !isMobileOnly) {
            setIsChatOpen(true);
        }
    }, [roomIsOwnedByUser]);

    // State values used for VideoChatContext
    const [isClientCameraOn, setIsClientCameraOn] = useState(false);
    useEffect(() => {
        if (isGuestsCameraDisabled) {
            setIsClientCameraOn(false);
        }
    }, [isGuestsCameraDisabled]);

    const [isClientMicOn, setIsClientMicOn] = useState(false);
    useEffect(() => {
        if (isGuestsMicDisabled) {
            setIsClientMicOn(false);
        }
    }, [isGuestsMicDisabled]);

    const [isClientScreenShared, setIsClientScreenShared] = useState(false);
    useEffect(() => {
        if (isScreenShareDisabled) {
            setIsClientScreenShared(false);
        }
    }, [isScreenShareDisabled]);

    const logEventsOnCallJoin = () => {
        const roomInfo = roomController.roomInfo;
        amplitudeService.logEvent(
            new JoinRoomCallEvent({
                roomId: roomId,
                performedAsHost: roomIsOwnedByUser,
                numRoomCallParticipants: roomController.numCallParticipants,
            }),
        );

        if (roomIsOwnedByUser) {
            // Log events for host starting a video call
            GA4Service.logEvent(GA4Event.HOST_START_ROOM_CALL, {
                [GA4EventProperties.rooms_info_id]: roomId,
                [GA4EventProperties.rooms_info_name]: roomInfo?.roomName,
                [GA4EventProperties.rooms_call_num_participants]: roomController.numCallParticipants,
            });
        } else {
            // Log events for a guest joining the video call
            GA4Service.logEvent(GA4Event.GUEST_JOIN_ROOM_CALL, {
                [GA4EventProperties.rooms_info_id]: roomId,
                [GA4EventProperties.rooms_info_name]: roomInfo?.roomName,
                [GA4EventProperties.rooms_call_num_participants]: roomController.numCallParticipants,
            });
        }
    };

    /**
     * Initiates a room call if initiated by the host, otherwise adds the user to
     * an existing call if they are a guest.
     * @returns True if the room is successfully started. False otherwise.
     */
    const handleStartRoomCall = useCallback(async () => {
        if (!isUserActiveProSubscriber(userData?.proUserInfo)) {
            setUpgradeCtaContext("unlimitedVideoChat", UpgradeCTASource.ROOMS);
            return false;
        } else if (roomController.isInteractingBlocked) {
            alert(BLOCKED_CALL_TOOLTIP_TEXT);
            return false;
        } else if (isVideoCallingDisabled) {
            alert(VIDEO_CALL_DISABLED_TOOLTIP_TEXT);
            return false;
        } else {
            setIsVideoChatLoading(true);
            const hasMediaPermissions = await requestMediaPermissions({
                audio: true,
                video: true,
            });

            if (!hasMediaPermissions) {
                alert(REQUIRES_MEDIA_PERMISSIONS_ERROR);
                openLinkNewTab(MIC_HELPDESK_LINK);
            } else if (roomId && userData) {
                await roomController
                    .generateAndJoinCall()
                    .then(() => {
                        logEventsOnCallJoin();
                        setIsVideoChatLoading(false);
                    })
                    .catch((error) => {
                        setIsVideoChatLoading(false);
                        alert(
                            `Error starting video call: ${error?.message}. ` +
                                `\n\nPlease try again. If you continue to have issues, reach ` +
                                `out to contact@lifeat.io for support.`,
                        );
                        logError(error);
                    });

                if (roomIsOwnedByUser) {
                    await RoomService.setIsRoomHostProUser(roomId, userData);
                }
            }

            return hasMediaPermissions;
        }
    }, [roomId, roomController, userData, isVideoCallingDisabled, roomIsOwnedByUser]);

    const [isVideoChatWidgetOpen, setIsVideoChatWidgetOpen] = useState(false);
    useEffect(() => {
        setIsVideoChatWidgetOpen(roomController.isVideoChatInit);
    }, [roomController.isVideoChatInit]);

    const participantsGuestData: { [id: string]: RoomGuest } = useMemo(() => {
        const data: { [id: string]: RoomGuest } = {};
        Object.values(roomGuests).forEach((rg: RoomGuest) => {
            data[rg.userEmail] = rg;
        });
        return data;
    }, [roomGuests]);

    const showJoinCallButton = !isVideoCallingDisabled && !roomController.isVideoChatInit && roomController.roomInfo;

    const [camDevice, setCamDevice] = useState<MediaDeviceInfo | null>(null);
    const [micDevice, setMicDevice] = useState<MediaDeviceInfo | null>(null);

    return (
        <VideoChatContext.Provider
            value={{
                isMyCameraOn: isClientCameraOn,
                setIsMyCameraOn: (on: boolean) => setIsClientCameraOn(on),
                selectedCameraDevice: camDevice,
                setSelectedCameraDevice: (device: MediaDeviceInfo) => setCamDevice(device),
                isMyMicOn: isClientMicOn,
                setIsMyMicOn: (on: boolean) => setIsClientMicOn(on),
                selectedMicDevice: micDevice,
                setSelectedMicDevice: (device: MediaDeviceInfo) => setMicDevice(device),
                isMyScreenShared: isClientScreenShared,
                setIsMyScreenShared: (share: boolean) => setIsClientScreenShared(share),
            }}
        >
            <ControlBarWrapper>
                <SocialControls>
                    {roomGuests && (
                        <RoomPresence roomGuests={roomGuests} roomOwnerUid={roomController.roomInfo?.ownerUid} />
                    )}

                    {/* Show loading animation while we wait for room data */}
                    {(!roomController || !roomController.roomInfo) && (
                        <ReactLoading type={"spokes"} color={"#E39685"} height={20} width={20} />
                    )}

                    {showJoinCallButton && (
                        <div
                            data-for={"block-call-tip"}
                            data-tip={
                                props.roomController.isInteractingBlocked
                                    ? BLOCKED_CALL_TOOLTIP_TEXT
                                    : !isUserActiveProSubscriber(userData?.proUserInfo)
                                      ? VIDEO_CALL_BLOCKED_PRO_TOOLTIP
                                      : ""
                            }
                        >
                            <LifeAtButtonV2
                                onClick={handleStartRoomCall}
                                size={"small"}
                                isDisabled={props.roomController.isInteractingBlocked}
                                isLoading={isVideoChatLoading}
                            >
                                <CameraIcon />
                                {videoCallParticipants?.length?.toString()}
                            </LifeAtButtonV2>
                            <ReactTooltip id={"block-call-tip"} />
                        </div>
                    )}

                    {roomId && (
                        <>
                            <ControlBarButtonDivider />
                            <RoomSelectorDropdown roomId={roomId} roomController={roomController} />
                        </>
                    )}

                    {/* Only show the Invite button / modal if the Room is owned by the user */}
                    {roomController.roomInfo && (
                        <>
                            <ControlBarButtonDivider />
                            <RoomInviteModal roomId={roomId} roomName={roomController.roomInfo?.roomName} />
                        </>
                    )}
                </SocialControls>
            </ControlBarWrapper>

            {roomController.token && (
                <RoomVideoChat
                    roomId={roomId}
                    roomController={roomController}
                    guestsData={participantsGuestData}
                    showParticipantsView={isVideoChatWidgetOpen}
                    setShowParticipantsView={(show: boolean) => setIsVideoChatWidgetOpen(show)}
                />
            )}
        </VideoChatContext.Provider>
    );
}
